// src/screens/admin/orderList/OrderList.jsx
import axios from "axios";
import {
  Check,
  CheckCircle,
  ChevronDown,
  Clock,
  Eye,
  Package,
  Palette,
  Search,
  Truck,
} from "lucide-react";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../../assets/css/admin/orders/AdminOrdersList.css";
import AppContext from "../../../context/AppContext";

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────
const FILTER_OPTIONS = [
  "This Month",
  "Last Month",
  "Last 3 Months",
  "Last 6 Months",
  "This Year",
  "All Time",
];
const STATUS_OPTIONS = [
  "All Orders",
  "pending",
  "in progress",
  "completed",
  "canceled",
];

const TYPE_OPTIONS = [
  { key: "all", label: "All Types" },
  { key: "normal", label: "Normal" },
  { key: "custom", label: "Customized" },
];

const STATUS_CONFIG = {
  pending: { icon: Clock, className: "ol-status--pending" },
  "in progress": { icon: Package, className: "ol-status--processing" },
  completed: { icon: CheckCircle, className: "ol-status--delivered" },
  canceled: { icon: null, className: "ol-status--cancelled" },
};

const ORDER_TYPE_CONFIG = {
  normal: { label: "Normal", icon: Package, className: "ol-type--normal" },
  custom: {
    label: "Customized",
    icon: Palette,
    className: "ol-type--custom",
  },
};

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
const filterByPeriod = (orders, period) => {
  if (period === "All Time") return orders;
  const now = new Date();
  const from = new Date();
  if (period === "This Month") from.setDate(1);
  if (period === "Last Month") {
    from.setMonth(from.getMonth() - 1);
    from.setDate(1);
  }
  if (period === "Last 3 Months") from.setMonth(from.getMonth() - 3);
  if (period === "Last 6 Months") from.setMonth(from.getMonth() - 6);
  if (period === "This Year") from.setMonth(0, 1);
  return orders.filter((o) => {
    const d = new Date(o.createdAt);
    return d >= from && d <= now;
  });
};

// ─────────────────────────────────────────────────────────────
// FILTER DROPDOWN (period)
// ─────────────────────────────────────────────────────────────
const FilterDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return (
    <div className="ol-filter-wrapper" ref={wrapperRef}>
      <button className="ol-filter-btn" onClick={() => setIsOpen((p) => !p)}>
        <span>{value}</span>
        <ChevronDown
          size={16}
          className={`ol-filter-chevron ${isOpen ? "open" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="ol-filter-menu">
          {FILTER_OPTIONS.map((opt) => (
            <div
              key={opt}
              className={`ol-filter-option ${value === opt ? "active" : ""}`}
              onClick={() => {
                onChange(opt);
                setIsOpen(false);
              }}
            >
              <span>{opt}</span>
              {value === opt && <Check size={14} className="ol-filter-check" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TYPE DROPDOWN
// ─────────────────────────────────────────────────────────────
const TypeDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const current = TYPE_OPTIONS.find((t) => t.key === value);
  return (
    <div className="ol-filter-wrapper" ref={wrapperRef}>
      <button className="ol-filter-btn" onClick={() => setIsOpen((p) => !p)}>
        <span>{current?.label || "All Types"}</span>
        <ChevronDown
          size={16}
          className={`ol-filter-chevron ${isOpen ? "open" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="ol-filter-menu">
          {TYPE_OPTIONS.map((opt) => (
            <div
              key={opt.key}
              className={`ol-filter-option ${value === opt.key ? "active" : ""}`}
              onClick={() => {
                onChange(opt.key);
                setIsOpen(false);
              }}
            >
              <span>{opt.label}</span>
              {value === opt.key && (
                <Check size={14} className="ol-filter-check" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STATUS BADGE
// ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status];
  const Icon = config?.icon;
  return (
    <span className={`ol-status-badge ${config?.className || ""}`}>
      {Icon && <Icon size={11} />}
      {status}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────
// ORDER TYPE BADGE
// ─────────────────────────────────────────────────────────────
const TypeBadge = ({ type }) => {
  const config = ORDER_TYPE_CONFIG[type];
  if (!config) return null;
  const Icon = config.icon;
  return (
    <span className={`ol-type-badge ${config.className}`}>
      <Icon size={10} />
      {config.label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────
// STATUS TABS
// ─────────────────────────────────────────────────────────────
const StatusTabs = ({ value, onChange, orders }) => {
  console.log(orders.find((o) => o.id === 19)?.adcart);
  const counts = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s] =
      s === "All Orders"
        ? orders.length
        : orders.filter((o) => o.status === s).length;
    return acc;
  }, {});
  return (
    <div className="ol-status-tabs" role="tablist">
      {STATUS_OPTIONS.map((s) => (
        <button
          key={s}
          role="tab"
          aria-selected={value === s}
          className={`ol-status-tab ${value === s ? "active" : ""}`}
          onClick={() => onChange(s)}
        >
          {s}
          <span className="ol-status-tab-count">{counts[s]}</span>
        </button>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────
const OrderList = () => {
  const { baseUrl, authToken, baseUrlForFile } = useContext(AppContext);
  const [orders, setOrders] = useState([]);
  const [filterPeriod, setFilterPeriod] = useState("All Time");
  const [statusFilter, setStatusFilter] = useState("All Orders");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    viewOrders();
  }, []);

  const viewOrders = () => {
    axios
      .get(`${baseUrl}/admin/order/viewOrders`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        setOrders(response.data.orders);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const visibleOrders = (() => {
    let base = filterByPeriod(orders, filterPeriod);
    if (statusFilter !== "All Orders")
      base = base.filter((o) => o.status === statusFilter);
    if (typeFilter !== "all") base = base.filter((o) => o.type === typeFilter);
    const q = searchQuery.trim().toLowerCase();
    return q
      ? base.filter(
          (o) =>
            o.id.toLowerCase().includes(q) ||
            o.adcustomer.fullNameame.toLowerCase().includes(q) ||
            o.adcart?.adproduct.name.toLowerCase().includes(q),
        )
      : base;
  })();

  const totalRevenue = orders.reduce(
    (sum, o) => (o.status !== "Canceled" ? sum + o.total : sum),
    0,
  );
  const completedCount = orders.filter((o) => o.status === "Completed").length;
  const pendingCount = orders.filter(
    (o) => o.status === "Pending" || o.status === "In Progress",
  ).length;

  return (
    <div className="ol-container">
      {/* ── Header ── */}
      <div className="ol-header">
        <div>
          <h1 className="ol-title">Order List</h1>
          <p className="ol-subtitle">Track and manage all customer orders</p>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="ol-stats-row">
        <div className="ol-stat-card">
          <div className="ol-stat-icon ol-stat-icon--orange">
            <Package size={18} />
          </div>
          <div>
            <p className="ol-stat-label">Total Orders</p>
            <p className="ol-stat-value">{orders.length}</p>
          </div>
        </div>
        <div className="ol-stat-card">
          <div className="ol-stat-icon ol-stat-icon--green">
            <CheckCircle size={18} />
          </div>
          <div>
            <p className="ol-stat-label">Completed</p>
            <p className="ol-stat-value">{completedCount}</p>
          </div>
        </div>
        <div className="ol-stat-card">
          <div className="ol-stat-icon ol-stat-icon--yellow">
            <Clock size={18} />
          </div>
          <div>
            <p className="ol-stat-label">Pending / In Progress</p>
            <p className="ol-stat-value">{pendingCount}</p>
          </div>
        </div>
        <div className="ol-stat-card">
          <div className="ol-stat-icon ol-stat-icon--orange">
            <Truck size={18} />
          </div>
          <div>
            <p className="ol-stat-label">Total Revenue</p>
            <p className="ol-stat-value ol-stat-value--revenue">
              PKR {totalRevenue.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* ── Status Tabs ── */}
      <StatusTabs
        value={statusFilter}
        onChange={setStatusFilter}
        orders={orders}
      />

      {/* ── Controls ── */}
      <div className="ol-controls">
        <div className="ol-search">
          <Search size={15} className="ad-ol-search-icon" />
          <input
            type="text"
            className="ol-search-input"
            placeholder="Search by order ID, customer or product..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="ol-controls-dropdowns">
          <TypeDropdown value={typeFilter} onChange={setTypeFilter} />
          <FilterDropdown value={filterPeriod} onChange={setFilterPeriod} />
        </div>
      </div>

      {/* ── Table ── */}
      <div className="ol-table-container">
        {visibleOrders.length === 0 ? (
          <div className="ol-empty">
            <span className="ol-empty-icon">📋</span>
            <h3>No orders found</h3>
            <p>Try adjusting your search or filter</p>
          </div>
        ) : (
          <table className="ol-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Product</th>
                <th>Type</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleOrders.map((o) => (
                <tr key={o.id}>
                  {/* Order ID */}
                  <td>
                    <span className="ol-order-id">{o.id}</span>
                  </td>

                  {/* Customer */}
                  <td>
                    <div className="ol-customer">
                      <div className="ol-avatar">
                        {o.adcustomer?.fullName // to get the initials of name
                          ?.split(" ")
                          .map((n) => n[0])
                          .slice(0, 2)
                          .join("")}
                      </div>
                      <div>
                        <div className="ol-customer-name">
                          {o.adcustomer?.fullName}
                        </div>
                        <div className="ol-customer-email">
                          {o.adcustomer?.email}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Product */}
                  <td>
                    <div className="ol-product">
                      <img
                        src={`${baseUrlForFile}/${o.adcart[0]?.adproduct?.productimages?.[0].image_path}`}
                        alt={o.adcart[0]?.adproduct?.name}
                        className="ol-product-img"
                      />
                      <div>
                        <div className="ol-product-name">
                          {o.adcart[0]?.adproduct?.name}
                        </div>
                        <div className="ol-product-category">
                          {o.adcart[0]?.adproduct?.adcategory.name}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Order Type Badge */}
                  <td>
                    <TypeBadge type={o.adcart[0]?.type} />
                  </td>

                  {/* Total */}
                  <td className="ol-total">
                    PKR {o.total_price.toLocaleString()}
                  </td>

                  {/* Payment */}
                  <td>
                    <span className="ol-payment-badge">
                      {o.paymentMethod || "---"}
                    </span>
                  </td>

                  {/* Date */}
                  <td className="ol-date">
                    {new Date(o.created_at).toLocaleDateString("en-PK", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>

                  {/* Status */}
                  <td>
                    <StatusBadge status={o.status} />
                  </td>

                  {/* Action */}
                  <td>
                    <button
                      className="ol-action-btn ol-view"
                      title="View Order"
                      onClick={() => navigate(`/admin/order-list/${o.id}`)}
                    >
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {visibleOrders.length > 0 && (
        <p className="ol-footer-count">
          Showing <strong>{visibleOrders.length}</strong> of{" "}
          <strong>{orders.length}</strong> orders
        </p>
      )}
    </div>
  );
};

export default OrderList;
